/**
 * Microsoft Entra ID (Azure AD) Authentication Configuration
 *
 * This file configures MSAL (Microsoft Authentication Library) for
 * authenticating users via Microsoft Entra ID.
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * MSAL configuration for the application
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          case LogLevel.Info:
            // Uncomment for verbose logging
            // console.info('[MSAL]', message);
            break;
          case LogLevel.Verbose:
            // console.debug('[MSAL]', message);
            break;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Scopes for login request
 * These are the permissions the app requests from the user
 * Includes the custom API scope to ensure consent at login time
 */
export const loginRequest = {
  scopes: [
    'User.Read',
    'openid',
    'profile',
    'email',
    // Include the API scope so users consent during initial login
    `api://${import.meta.env.VITE_ENTRA_CLIENT_ID}/access_as_user`,
  ],
};

/**
 * Scopes for acquiring access tokens for API calls
 * Uses the custom API scope exposed in Azure Portal
 */
export const apiRequest = {
  scopes: [`api://${import.meta.env.VITE_ENTRA_CLIENT_ID}/access_as_user`],
};

/**
 * Validate that required environment variables are set
 */
export function validateAuthConfig(): boolean {
  const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
  const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID;

  if (!clientId || !tenantId) {
    console.error(
      'Missing Entra ID configuration. Please set VITE_ENTRA_CLIENT_ID and VITE_ENTRA_TENANT_ID environment variables.'
    );
    return false;
  }

  return true;
}
